import { createFileRoute, redirect } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  TablePagination,
  Snackbar,
  Alert,
} from "@mui/material";
import { z } from "zod";
import ListLayout from "../../../components/Layout/ListLayout";
import TenantList, { type Order } from "../../../components/Admin/Tenants/TenantList";
import TenantForm, { type TenantFormValues } from "../../../components/Admin/Tenants/TenantForm";
import TenantFilters, { type TenantFilterValues } from "../../../components/Admin/Tenants/TenantFilters";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  useListAllTenant,
  useEditTenant,
  useEnableTenant,
  useDisableTenant,
  getTenant
} from "../../../api/bpm/endpoints";
import type { SmartTenantDto } from "../../../api/bpm/model";
import { useTranslation } from "react-i18next";

const tenantsSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
  orderBy: z.string().catch('name'),
  order: z.enum(['asc', 'desc']).catch('asc'),
});

export const Route = createFileRoute('/__auth/admin/tenants')({
  component: TenantsComponent,
  validateSearch: (search) => tenantsSearchSchema.parse(search),
  beforeLoad: ({ context }) => {
    if (context.auth.isLoading) return;
    if (!context.auth.user?.isAdmin) {
      throw redirect({
        to: "/dashboard",
      });
    }
  },
  staticData: {
    title: "routes.tenants",
  },
});

function TenantsComponent() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const [isTenantDialogOpen, setIsTenantDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<SmartTenantDto | null>(null);
  const [loadingTenantDetails, setLoadingTenantDetails] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const { data: rawTenantsData, isLoading: loadingTenants, refetch } = useListAllTenant();

  const tenants: SmartTenantDto[] = useMemo(() => {
    if (!rawTenantsData) return [];
    if (Array.isArray(rawTenantsData)) return rawTenantsData;
    if (Array.isArray((rawTenantsData as any).data)) return (rawTenantsData as any).data;
    return [];
  }, [rawTenantsData]);

  const editMutation = useEditTenant();
  const enableMutation = useEnableTenant();
  const disableMutation = useDisableTenant();

  const isMutationLoading = editMutation.isPending || enableMutation.isPending || disableMutation.isPending;

  const sortedAndFilteredTenants = useMemo(() => {
    let result = [...tenants];

    if (search.q) {
      const query = search.q.toLowerCase();
      result = result.filter((t: any) =>
        (t.name || "").toLowerCase().includes(query) ||
        (t.id || "").toLowerCase().includes(query)
      );
    }

    const { orderBy, order } = search;
    result.sort((a: any, b: any) => {
      const valueA = a[orderBy] || '';
      const valueB = b[orderBy] || '';

      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [tenants, search.q, search.orderBy, search.order]);

  const paginatedData = useMemo(() => {
    const start = (search.page - 1) * search.size;
    const end = start + search.size;
    return sortedAndFilteredTenants.slice(start, end);
  }, [sortedAndFilteredTenants, search.page, search.size]);

  const handleEditTenant = useCallback(async (tenant: SmartTenantDto) => {
    if (!tenant.id) return;
    setLoadingTenantDetails(true);
    try {
      const response = await getTenant(tenant.id);
      const resolvedTenant = (response as any)?.data || response;
      setSelectedTenant(resolvedTenant);
      setIsTenantDialogOpen(true);
    } catch (err: any) {
      const errorMessage = err.body?.message || err.message || t("admin.tenants.errorLoading");
      showSnackbar(errorMessage, "error");
    } finally {
      setLoadingTenantDetails(false);
    }
  }, [t]);

  const handleTenantFormSubmit = async (values: TenantFormValues, adminUsers?: any[]) => {
    if (!selectedTenant || !selectedTenant.id) return;
    try {
      await editMutation.mutateAsync({
        tenantId: selectedTenant.id,
        data: {
          name: values.name,
          adminUsers: adminUsers?.map((u) => ({ id: u.id || "" })) || [],
        }
      });
      showSnackbar(t("admin.tenants.updatedSuccess"));
      refetch();
      setIsTenantDialogOpen(false);
      setSelectedTenant(null);
    } catch (err: any) {
      const errorMessage = err.body?.message || err.message || t("admin.tenants.errorSaving");
      showSnackbar(errorMessage, "error");
    }
  };

  const handleToggleStatus = async (tenant: SmartTenantDto) => {
    if (!tenant.id) return;
    try {
      if (tenant.enabled) {
        await disableMutation.mutateAsync({
          tenantId: tenant.id,
        });
        showSnackbar(t("admin.organizations.inactivatedSuccess"));
      } else {
        await enableMutation.mutateAsync({
          tenantId: tenant.id,
        });
        showSnackbar(t("admin.organizations.activatedSuccess"));
      }
      refetch();
    } catch (err: any) {
      const errorMessage = err.body?.message || err.message || t("admin.organizations.errorChangingStatus");
      showSnackbar(errorMessage, "error");
    }
  };

  const handlePageChange = (_e: any, newPage: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: newPage + 1 }),
    });
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    navigate({
      search: (prev) => ({ ...prev, size: parseInt(e.target.value, 10), page: 1 }),
    });
  };

  const handleFilter = (filters: TenantFilterValues) => {
    navigate({
      search: (prev) => ({ ...prev, q: filters.q || undefined, page: 1 }),
    });
  };

  const handleRequestSort = (property: string) => {
    const isAsc = search.orderBy === property && search.order === 'asc';
    navigate({
      search: (prev) => ({
        ...prev,
        orderBy: property,
        order: isAsc ? 'desc' : 'asc',
      }),
    });
  };

  return (
    <ListLayout
      title={t("admin.tenants.title")}
      subtitle={t("admin.tenants.subtitle")}
      isLoading={loadingTenants || isMutationLoading || loadingTenantDetails}
      filters={
        <TenantFilters
          onFilter={handleFilter}
          isLoading={loadingTenants}
          initialValues={{ q: search.q }}
        />
      }
    >
      <TenantList
        tenants={paginatedData}
        orderBy={search.orderBy}
        order={search.order as Order}
        onRequestSort={handleRequestSort}
        onEdit={handleEditTenant}
        onToggleStatus={handleToggleStatus}
      />

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={sortedAndFilteredTenants.length}
        rowsPerPage={search.size}
        page={search.page - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        labelRowsPerPage={t("common.rowsPerPage") || "Linhas por página"}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />

      <Dialog
        open={isTenantDialogOpen}
        onClose={() => !isMutationLoading && setIsTenantDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 4 } }}
      >
        <DialogContent>
          <TenantForm
            initialData={selectedTenant ? { id: selectedTenant.id, name: selectedTenant.name || "" } : undefined}
            adminUsers={(selectedTenant as any)?.adminUsers || []}
            onSubmit={handleTenantFormSubmit}
            onCancel={() => setIsTenantDialogOpen(false)}
            isLoading={editMutation.isPending}
            loadingDetails={loadingTenantDetails}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
